import React, { useState, useMemo, useEffect } from "react";
import { userApi } from "../../../backend_urls/userApi";
import { useNavigate } from "react-router-dom";
import { UserURL } from "../../../routes/user_routes/UserURL";
import BaseIcon from "../../../components/svg_icons/BaseIcon";
import DefaultTooltip from "../../../components/tool_tips/DefaultTooltip";
import DefaultPopUp from "../../../components/popups/DefaultPopUp";
import { formatMoneyWithCurrency } from "../../../utils/formatter";
import LoadingSpinner from "../../../components/loaders/LoadingSpinner";
import "../../../css/pages/user_pages/modals/RecordsTable.css";

const ITEMS_PER_PAGE = 10;

const RecordsTable = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [fetchedData, setData] = useState([]);
    const [filter, setFilter] = useState("All");
    const [sortKey, setSortKey] = useState("date");
    const [sortOrder, setSortOrder] = useState("desc");
    const [page, setPage] = useState(1);

    const [deleteRecordId, setDeleteRecordId] = useState(null);
    const [deletePopUp, setDeletePopUp] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await userApi.getAllInvoices();
            setData(res.data || res);
        } catch (error) { console.error("Error fetching invoices:", error);
        } finally { setLoading(false); }
    };
    useEffect(() => { fetchData(); }, []);

    // 🔍 FILTER + SORT
    const processedData = useMemo(() => {
        let data = [...fetchedData];

        // FILTER
        if (filter !== "All") { data = data.filter((item) => item.status === filter); }

        // SORT
        data.sort((a, b) => {
            let valA = a[sortKey];
            let valB = b[sortKey];
            if (sortKey === "date") {
                valA = new Date(valA);
                valB = new Date(valB);
            }
            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
        return data;
    }, [fetchedData, filter, sortKey, sortOrder]);

    // 📄 PAGINATION
    const totalPages = Math.ceil(processedData.length / ITEMS_PER_PAGE);

    const paginatedData = processedData.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortOrder("asc");
        }
    };
    const handleDelete = async () => {
        try {
            setLoading(true);
            await userApi.deleteInvoice(deleteRecordId)
            fetchData();
        } catch (error) { console.error("Error deleting record:", error);
        } finally {
            setLoading(false);
            setDeletePopUp(false);
            setDeleteRecordId(null);
        }
    };
    const handleEditClick = (item) => {
        setLoading(true);
        navigate(UserURL.user_invoice_edit(item.business, item.id));
    };

    return ( loading ? <LoadingSpinner /> :
        <>
            <div className="table-card">
                {/* HEADER */}
                <div className="table-header">
                    <h3>Invoice Records</h3>

                    <div className="table-controls">
                        <div className="search">
                            🔍 <input placeholder="Search records..." />
                        </div>

                        {/* FILTER DROPDOWN */}
                        <div className="dropdown">
                            <button className="filter-btn">Filter ▾</button>
                            <div className="dropdown-menu">
                            {["All", "Processed", "Pending", "Failed"].map((f) => (
                                <div key={f} onClick={() => { setFilter(f); setPage(1); }}>
                                {f}
                                </div>
                            ))}
                            </div>
                        </div>

                        {/* SORT DROPDOWN */}
                        <div className="dropdown">
                            <button className="filter-btn">Sort ▾</button>
                            <div className="dropdown-menu">
                            <div onClick={() => handleSort("date")}>Date</div>
                            <div onClick={() => handleSort("name")}>Name</div>
                            <div onClick={() => handleSort("amount")}>Amount</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <table className="activity-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort("date")}>
                                <DefaultTooltip text="Date of the invoice">
                                    ADDED ON
                                </DefaultTooltip>
                            </th>
                            <th onClick={() => handleSort("amount")}>
                                <DefaultTooltip text="Amount which will be invoice created">
                                    Invoice Value
                                </DefaultTooltip>
                            </th>
                            <th>
                                <DefaultTooltip text="Source of record generation">
                                    SOURCE
                                </DefaultTooltip>
                            </th>
                            <th>
                                <DefaultTooltip text="Status of the record">
                                    STATUS
                                </DefaultTooltip>
                            </th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((item, index) => (
                            <tr key={index}>
                                <td>{new Date(item.created_at).toDateString()}</td>
                                <td className="amount">
                                    {   
                                        formatMoneyWithCurrency(
                                            item.invoice?.grand_total || 0,
                                            item.invoice?.currency || "INR"
                                        )
                                    }
                                </td>
                                <td>
                                    {
                                    item.invoice?.source ?
                                    item.invoice.source === "upload" ? "WhatsApp Upload" :
                                    item.invoice.source === "api" ? "API" :
                                    "Unknown" : "Unknown"
                                    }
                                </td>
                                <td>
                                    <span className='status upload'>
                                        ● { item.invoice?.source }
                                    </span>
                                </td>
                                <td className="actions">
                                    <button
                                        className="action-icon-btn eyeOpen" title="View"
                                        onClick={() => handleEditClick(item)}
                                    >
                                        <BaseIcon name="writingHand" size="20" />
                                    </button>
                                    <button
                                        className="action-icon-btn delete" title="Delete"
                                        onClick={() => {
                                            setDeletePopUp(true)
                                            setDeleteRecordId(item.id);
                                        }}
                                    >
                                        <BaseIcon name="trash" size="20" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* FOOTER */}
                <div className="table-footer">
                    <span>
                        Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                        {Math.min(page * ITEMS_PER_PAGE, processedData.length)} of{" "}
                        {processedData.length} entries
                    </span>

                    <div className="pagination">
                        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                            {"<"}
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i}
                                className={page === i + 1 ? "active" : ""}
                                onClick={() => setPage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button
                            disabled={page === totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            {">"}
                        </button>
                    </div>
                </div>
            </div>
            <DefaultPopUp
                isOpen={deletePopUp}
                onClose={() => setDeletePopUp(false)}
                title="Delete Record?"
                subtext="Are you sure you want to delete this record? This action cannot be undone."
                cancelText="Cancel"
                actionText="Delete"
                onAction={() => { handleDelete(); }}
            />
        </>
    );
};

export default RecordsTable;
